import { useEffect, useState } from "react";
import styles from "./CommunityContentPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import videoPlaceholderImage from "../../assets/images/video-player-placeholder.png";
import { FaSpinner } from "react-icons/fa";
import PageContentSections, { toSections } from "./PageContentSections";

/* ---------------- TEXT FORMATTER ---------------- */
const formatBoldText = (text = "") =>
  text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

/* ---------------- DETECT PLATFORM ---------------- */
const isMobileApp = () => {
  return /flutter|webview/i.test(navigator.userAgent);
};

/* ---------------- THUMBNAILS ---------------- */
const getYoutubeThumbnail = (url) => {
  const match = url.match(/(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

const getVimeoThumbnail = async (url) => {
  const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
  const match = url.match(regex);

  if (!match || !match[1]) return null;

  const videoId = match[1];

  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`,
    );

    if (response.ok) {
      const data = await response.json();
      if (data.thumbnail_url) {
        return data.thumbnail_url;
        // .replace("_295x166", "_640x360")
        // .replace("_640", "_1280");
      }
    }
  } catch (error) {
    console.error("Vimeo API error:", error);
  }

  return `https://vumbnail.com/${videoId}.jpg`;
};

const getVideoThumbnail = async (url) => {
  if (url.includes("vimeo")) return await getVimeoThumbnail(url);
  if (url.includes("youtube") || url.includes("youtu.be"))
    return getYoutubeThumbnail(url);
  return videoPlaceholderImage;
};

/* ================================================= */

const CommunityContentPage = () => {
  const { communityIndex, communities } = useDashboardStore();
  const { user, userMeta, refreshIapStatus } = useAuthStore();

  const [communityData, setCommunityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [thumbnailCache, setThumbnailCache] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // 🔐 Membership purchase states
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const community = communities[communityIndex];
  const role = user?.roles?.[0];

  // Fetch full community data with video_tabs
  useEffect(() => {
    const fetchCommunityData = async () => {
      if (!community?.id) return;

      setIsLoading(true);
      try {
        const { data } = await api.get(
          ENDPOINTS.GET_COMMUNITY_BY_ID(community.id),
        );
        if (data.success) {
          setCommunityData(data.community);
        }
      } catch (error) {
        console.error("Error fetching community data:", error);
        toast.error("Failed to load community content");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommunityData();
  }, [community?.id]);

  useEffect(() => {
    refreshIapStatus();
  }, []);

  // Use fetched data if available, otherwise fall back to store data
  const displayCommunity = communityData;

  let userMembershipIds = [];
  if (Array.isArray(userMeta?.membership_ids)) {
    userMembershipIds = userMeta.membership_ids.map(Number);
  } else if (typeof userMeta?.membership_ids === "string") {
    try {
      userMembershipIds = JSON.parse(userMeta.membership_ids).map(Number);
    } catch {
      userMembershipIds = [];
    }
  }

  const sections = toSections(displayCommunity);

  /* ---------------- PRELOAD THUMBNAILS ---------------- */
  useEffect(() => {
    const secs = toSections(displayCommunity);
    if (secs.length === 0) return;

    const load = async () => {
      const cache = {};
      for (const sec of secs) {
        for (const tab of sec.video_tabs || []) {
          for (const group of tab.groups || []) {
            for (const video of group.videos || []) {
              if (!video.cover_image && video.url) {
                const key = video.id || video.url;
                cache[key] =
                  (await getVideoThumbnail(video.url)) || videoPlaceholderImage;
              }
            }
          }
        }
      }
      setThumbnailCache(cache);
    };

    load();
  }, [displayCommunity]);

  /* ---------------- PURCHASE ---------------- */
  const handleMembershipClick = async (membershipIds) => {
    if (isPurchasing || isLoadingPlans) return;

    setIsLoadingPlans(true);

    try {
      if (!membershipIds?.length) {
        toast.error("No membership plans configured for this video");
        return;
      }

      if (membershipIds.length === 1) {
        handlePurchaseMembership(membershipIds[0]);
      } else {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS, {
          params: { hide_restricted: true },
        });
        const allPlans = data.memberships || [];

        const filteredPlans = allPlans.filter((plan) =>
          membershipIds.includes(plan.id),
        );

        setAvailablePlans(filteredPlans);
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error loading membership plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;

    setIsPurchasing(true);

    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });

      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link",
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase");
    } finally {
      setIsPurchasing(false);
    }
  };

  /* ---------------- VIDEO CLICK ---------------- */

  const handleAttachmentClick = async (e, fileUrl, video) => {
    let hasAccess = true;

    if (video.membership_ids?.length) {
      if (role === "administrator") {
        hasAccess = true;
      } else if (role === "trader") {
        hasAccess = await checkTraderAccessToCommunity();
      } else {
        // ✅ IAP users get full access (paid from mobile)
        if (userMeta?.has_active_iap === true) {
          hasAccess = true;
        } else {
          const normalizedVideoMembershipIds = (video.membership_ids || [])
            .map((id) => {
              const num = Number(id);
              return isNaN(num) ? 0 : num;
            })
            .filter((id) => id > 0);

          hasAccess = normalizedVideoMembershipIds.some((id) =>
            userMembershipIds.includes(id),
          );
        }
      }
    }

    if (!hasAccess) {
      toast.error("File is restricted");
      handleMembershipClick(video.membership_ids);
      return;
    } else {
      e.stopPropagation();

      // your logic here
      window.open(fileUrl, "_blank");
    }
  };

  const handleVideoClick = async (video) => {
    const isAdmin = role === "administrator";
    const hasActiveIap = userMeta?.has_active_iap === true;

    let hasAccess = true;

    const expiry = video.membership_expiry || [];

    if (expiry.length > 0) {
      if (isAdmin) {
        hasAccess = true;
      } else if (role === "trader") {
        hasAccess = await checkTraderAccessToCommunity();
      } else if (hasActiveIap) {
        hasAccess = true;
      } else {
        const now = new Date();
        hasAccess = false;
        for (const entry of expiry) {
          const { membership_id, expires_at } = entry;
          if (membership_id === 0) {
            if (!expires_at || new Date(expires_at) > now) {
              hasAccess = true;
              break;
            }
            continue;
          }
          if (userMembershipIds.includes(Number(membership_id))) {
            if (!expires_at || new Date(expires_at) > now) {
              hasAccess = true;
              break;
            }
          }
        }
      }
    }

    if (!hasAccess) {
      toast.error("Video is restricted");
      const paidIds = expiry
        .filter((e) => e.membership_id !== 0)
        .map((e) => e.membership_id);
      handleMembershipClick(paidIds);
      return;
    }

    if (video.url.includes("vimeo")) {
      const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
      const match = video.url.match(regex);
      if (match && match[1]) {
        setSelectedVideo({ type: "vimeo", id: match[1], hash: match[2] });
        setShowVideoModal(true);
      }
    } else {
      window.open(video.url, "_blank", "noopener,noreferrer");
    }
  };

  /* ---------------- CHECK TRADER ACCESS ---------------- */
  const checkTraderAccessToCommunity = async () => {
    try {
      const { channels } = useDashboardStore.getState();

      const communityChannels = channels.filter(
        (channel) => channel.community === displayCommunity.name,
      );

      if (communityChannels.length === 0) {
        return false;
      }

      const channelIds = communityChannels.map((c) => c.id);

      const { data } = await api.post(ENDPOINTS.GET_CHANNELS_BY_IDS, {
        channelIds,
      });

      if (data.success) {
        const channelsData = data.channels || [];

        const isAssignedToAnyCommunityChannel = channelsData.some((channel) => {
          const traderIds = channel.trader_ids || [];
          return traderIds.includes(user.id);
        });

        return isAssignedToAnyCommunityChannel;
      }

      return false;
    } catch (error) {
      console.error("Error checking trader access to community:", error);
      return false;
    }
  };

  const hasContent = sections.length > 0;

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>
            {communityData?.name ?? community?.name}
          </h2>
        </div>
        <div className={styles.scrollableContent}>
          <div style={{ textAlign: "center", padding: "50px" }}>
            <FaSpinner className="fa-spin" style={{ fontSize: "48px" }} />
            <p style={{ marginTop: "20px" }}>Loading community content...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>{displayCommunity?.name}</h2>
      </div>

      <div className={styles.scrollableContent}>
        {!hasContent && <p>No content available for this community</p>}

        <PageContentSections
          sections={sections}
          thumbnailCache={thumbnailCache}
          role={role}
          userMembershipIds={userMembershipIds}
          onVideoClick={handleVideoClick}
          onAttachmentClick={handleAttachmentClick}
        />
      </div>

      {/* ---------------- PLAN SELECTION MODAL ---------------- */}
      {showPlanSelection && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupContent}>
            <h3>Choose a Membership Plan</h3>

            {availablePlans.map((plan) => (
              <div key={plan.id} style={{ marginBottom: 16 }}>
                <h4>{plan.name || plan.label}</h4>
                <p>
                  {plan.payment_type === "free" ? "Free" : `$${plan.price}`}
                </p>
                <button
                  className={styles.popupButton}
                  disabled={isPurchasing}
                  onClick={() => {
                    handlePurchaseMembership(plan.id);
                    setShowPlanSelection(false);
                  }}
                >
                  {isPurchasing ? (
                    <>
                      <FaSpinner className="fa-spin" /> Processing...
                    </>
                  ) : (
                    "Select Plan"
                  )}
                </button>
              </div>
            ))}

            <button
              className={styles.popupButton}
              onClick={() => setShowPlanSelection(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ---------------- VIMEO MODAL (WEBVIEW) ---------------- */}
      {showVideoModal && selectedVideo && (
        <div
          className={styles.videoModalOverlay}
          onClick={() => {
            setShowVideoModal(false);
            setSelectedVideo(null);
          }}
        >
          <div
            className={styles.videoModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
            >
              ✕
            </button>
            {selectedVideo.type === "vimeo" && (
              <iframe
                src={
                  selectedVideo.hash
                    ? `https://player.vimeo.com/video/${selectedVideo.id}?h=${selectedVideo.hash}&autoplay=1&title=0&byline=0&portrait=0&controls=1`
                    : `https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0&controls=1`
                }
                className={styles.videoIframe}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Vimeo Video"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityContentPage;
